import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import type { CanvasPosition } from '../../../../types/infrastructure-map'

export const TILE_WIDTH = 80
export const TILE_HEIGHT = 48
export const COUNT_TEXT_OFFSET_Y = 4
export const LABEL_TEXT_OFFSET_Y = 12
export const INDICATOR_INSET = 8
export const HIT_AREA_PADDING = 4
export const HIT_AREA_EXTENT = HIT_AREA_PADDING * 2
const BACKGROUND_COLOR = 'rgb(241, 245, 249)'
const BORDER_COLOR = 'rgb(148, 163, 184)'
const TEXT_COLOR = 'rgb(71, 85, 105)'
const EXTERNAL_LINK_COLOR = 'rgb(245, 158, 11)'

interface BackgroundProps {
  position: CanvasPosition
}

export function TileBackground({ position }: BackgroundProps): ReactElement {
  return (
    <rect
      x={position.x - TILE_WIDTH / 2}
      y={position.y - TILE_HEIGHT / 2}
      width={TILE_WIDTH}
      height={TILE_HEIGHT}
      rx={8}
      ry={8}
      fill={BACKGROUND_COLOR}
      stroke={BORDER_COLOR}
      strokeWidth={2}
      strokeDasharray="4 2"
      className="transition-all duration-150 hover:stroke-fuchsia-400 dark:fill-slate-700 dark:stroke-slate-500"
    />
  )
}

interface LabelsProps {
  position: CanvasPosition
  displayCount: number
  t: TFunction<'cases'>
}

export function TileLabels({ position, displayCount, t }: LabelsProps): ReactElement {
  return (
    <>
      <text
        x={position.x}
        y={position.y - COUNT_TEXT_OFFSET_Y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={TEXT_COLOR}
        fontSize={16}
        fontWeight="bold"
        className="pointer-events-none select-none dark:fill-slate-300"
      >
        {displayCount}
      </text>
      <text
        x={position.x}
        y={position.y + LABEL_TEXT_OFFSET_Y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={TEXT_COLOR}
        fontSize={10}
        className="pointer-events-none select-none dark:fill-slate-400"
      >
        {displayCount === 1
          ? t('infrastructure.canvas.child')
          : t('infrastructure.canvas.children')}
      </text>
    </>
  )
}

interface IndicatorProps {
  position: CanvasPosition
}

export function ExternalLinkIndicator({ position }: IndicatorProps): ReactElement {
  return (
    <g>
      <circle
        cx={position.x + TILE_WIDTH / 2 - INDICATOR_INSET}
        cy={position.y - TILE_HEIGHT / 2 + INDICATOR_INSET}
        r={6}
        fill={EXTERNAL_LINK_COLOR}
        stroke="white"
        strokeWidth={1.5}
      />
      <text
        x={position.x + TILE_WIDTH / 2 - INDICATOR_INSET}
        y={position.y - TILE_HEIGHT / 2 + INDICATOR_INSET}
        textAnchor="middle"
        dominantBaseline="central"
        fill="white"
        fontSize={8}
        fontWeight="bold"
        className="pointer-events-none select-none"
      >
        {'\u2197'}
      </text>
    </g>
  )
}

interface HitAreaProps {
  position: CanvasPosition
  displayCount: number
  t: TFunction<'cases'>
}

export function TileHitArea({ position, displayCount, t }: HitAreaProps): ReactElement {
  return (
    <rect
      x={position.x - TILE_WIDTH / 2 - HIT_AREA_PADDING}
      y={position.y - TILE_HEIGHT / 2 - HIT_AREA_PADDING}
      width={TILE_WIDTH + HIT_AREA_EXTENT}
      height={TILE_HEIGHT + HIT_AREA_EXTENT}
      fill="transparent"
    >
      <title>{t('infrastructure.canvas.clickToExpand', { count: displayCount })}</title>
    </rect>
  )
}
